import React, { useMemo, useCallback } from "react";
import { format } from "date-fns";
import {
  generateTimeSlots,
  generateWeekDays,
  formatHour,
  TIME_SLOT_CONFIG,
} from "./calendarUtils";
import { Thumbnail } from "../Thumbnail";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@exiftrator/shared/components/ui";
import { useIsMobile, useIsTablet } from "../../hooks/useMobile";
import {
  WORKSPACE_SPACING,
  getDensityClass,
} from "../Workspace/workspaceSpacing";

const TimeSlotCell = React.memo(
  ({
    day,
    hour,
    hourKey,
    isDragOver,
    files,
    onSlotClick,
    onDragOver,
    onDragLeave,
    onDrop,
    onFileDragStart,
  }) => {
    const hasFiles = files.length > 0;
    const maxVisible = 4;
    const visibleFiles = useMemo(() => files.slice(0, maxVisible), [files]);
    const overflowCount = files.length - maxVisible;

    return (
      <div
        className={`min-h-[60px] p-1 cursor-pointer transition-all flex flex-col ${
          isDragOver
            ? "bg-accent-primary-glow ring-2 ring-inset ring-accent-primary ring-dashed"
            : "hover:bg-bg-tertiary"
        } ${
          hasFiles
            ? "bg-bg-secondary border-l-2 border-accent-primary-dim"
            : "bg-bg-secondary/50"
        }`}
        onClick={(e) => onSlotClick(day, hour, e)}
        onDragOver={(e) => onDragOver(e, day, hour)}
        onDragLeave={onDragLeave}
        onDrop={(e) => onDrop(e, day, hour)}
      >
        {hasFiles && (
          <TooltipProvider delayDuration={300}>
            {/* Header row: hour on left, file count on right */}
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] text-text-supporting font-mono">
                {formatHour(hour)}
              </span>
              <span className="text-[10px] text-text-supporting font-mono">
                {files.length} {files.length === 1 ? "file" : "files"}
              </span>
            </div>

            {/* Thumbnail grid - 2x2 */}
            <div className="grid grid-cols-2 gap-[2px] w-full">
              {visibleFiles.map(({ file, index }, i) => {
                const isLastWithOverflow = i === maxVisible - 1 && overflowCount > 0;
                return (
                  <Tooltip key={index}>
                    <TooltipTrigger asChild>
                      <div
                        className="relative aspect-square"
                        draggable={true}
                        onDragStart={(e) => onFileDragStart(e, index)}
                      >
                        <Thumbnail
                          src={file.thumbnailUrl}
                          alt={file.name}
                          size="fill"
                        />
                        {isLastWithOverflow && (
                          <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-[2px]">
                            <span className="text-white text-[10px] font-semibold">
                              +{overflowCount}
                            </span>
                          </div>
                        )}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs">
                      {file.name}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </TooltipProvider>
        )}
      </div>
    );
  },
);

const DayHeader = React.memo(({ day, fileCount }) => {
  const dayName = format(day, "EEE");
  const dateNum = format(day, "d");
  const isToday =
    format(day, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");

  return (
    <div
      className={`bg-bg-tertiary p-2 text-center ${isToday ? "bg-accent-primary-glow" : ""}`}
    >
      <div
        className={`text-[10px] font-semibold uppercase tracking-widest ${isToday ? "text-text-accent" : "text-text-supporting"}`}
      >
        {dayName}
      </div>
      <div
        className={`text-[14px] font-semibold ${isToday ? "text-text-accent" : "text-text-secondary"}`}
      >
        {dateNum}
      </div>
      {fileCount > 0 && (
        <div className="text-[9px] text-text-accent font-mono mt-[2px]">
          {fileCount} file{fileCount !== 1 ? "s" : ""}
        </div>
      )}
    </div>
  );
});

function WeeklyGrid({
  currentWeek,
  groupedByHour,
  groupedByDate,
  dragOverTarget,
  onSlotClick,
  onDragOver,
  onDragLeave,
  onDrop,
  onFileDragStart,
}) {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const marginClass = getDensityClass(
    isMobile,
    isTablet,
    WORKSPACE_SPACING.calendarCanvasMargin,
  );
  const weekDays = useMemo(() => generateWeekDays(currentWeek), [currentWeek]);
  const timeSlots = useMemo(
    () =>
      generateTimeSlots(
        TIME_SLOT_CONFIG.startHour,
        TIME_SLOT_CONFIG.endHour,
        TIME_SLOT_CONFIG.slotDurationMinutes,
      ),
    [],
  );

  const renderRow = useCallback(
    (index) => {
      const { hour } = timeSlots[index];

      return (
        <div
          key={index}
          className="grid grid-cols-[60px_repeat(7,1fr)] gap-px bg-border-default border-b border-border-default last:border-b-0"
        >
          {/* Time label */}
          <div className="bg-bg-tertiary p-2 flex items-start justify-end pr-4 h-full min-h-[60px]">
            <span className="text-[10px] text-text-supporting font-mono">
              {formatHour(hour)}
            </span>
          </div>

          {/* Day cells for this hour */}
          {weekDays.map((day, dayIndex) => {
            const hourKey =
              format(day, "yyyy-MM-dd") + `T${String(hour).padStart(2, "0")}`;
            const isDragOver = dragOverTarget === hourKey;
            const files = groupedByHour.get(hourKey) || [];

            return (
              <TimeSlotCell
                key={dayIndex}
                day={day}
                hour={hour}
                hourKey={hourKey}
                isDragOver={isDragOver}
                files={files}
                onSlotClick={onSlotClick}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                onFileDragStart={onFileDragStart}
              />
            );
          })}
        </div>
      );
    },
    [
      timeSlots,
      weekDays,
      groupedByHour,
      dragOverTarget,
      onSlotClick,
      onDragOver,
      onDragLeave,
      onDrop,
      onFileDragStart,
    ],
  );

  return (
    <div
      className={`${marginClass} border border-border-default overflow-hidden bg-bg-primary flex flex-col flex-1 rounded-sm`}
    >
      {/* Time rows containing header and body */}
      <div className="flex-1 overflow-auto bg-bg-primary">
        <div className={isMobile ? "min-w-[760px]" : ""}>
          {/* Header row - sticky to stay at top while scrolling */}
          <div className="grid grid-cols-[60px_repeat(7,1fr)] gap-px bg-border-default sticky top-0 z-10 border-b border-border-default shrink-0">
            <div className="bg-bg-tertiary p-2" /> {/* Empty corner */}
            {weekDays.map((day, index) => {
              const dateKey = format(day, "yyyy-MM-dd");
              const filesForDay = groupedByDate.get(dateKey) || [];
              return (
                <DayHeader key={index} day={day} fileCount={filesForDay.length} />
              );
            })}
          </div>

          <div className="bg-border-default">
            {timeSlots.map((_, index) => renderRow(index))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default WeeklyGrid;
